import * as THREE from 'three';
import {GUI} from 'dat.gui/build/dat.gui.module.js';

import {AlphaBatem} from "@alphabatem/js_sdk/src/alphabatem";

import {PlayerHandler} from "./handlers/player_handler.js";
import {ItemHandler} from "./handlers/item_handler.js";
import {AdvertisingHandler} from "./handlers/advertising_handler.js";
import {ThreeObserverHandler} from "./devices/device_observer";
import {ThreeGeneralBuilderHandler} from "./devices/device_general_builder";
import {ThreeMinecraftHandler} from "./devices/device_minecraft";
import {Three3DHandler} from "./devices/device_3d";
import {ThreePlayer} from "./entities/player";
import {DragControls} from "three/examples/jsm/controls/DragControls";
import {TransformControls} from "three/examples/jsm/controls/TransformControls";
import {ThreeThirdPersonHandler} from "./devices/device_third_person";

import {CSS2DRenderer} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {CollisionHandler} from "./handlers/collision_handler";
import {InteractionHandler} from "./handlers/interaction_handler";
import {AnimationHandler} from "./handlers/animation_handler";

//Rendering
import {EffectComposer} from "three/examples/jsm/postprocessing/EffectComposer";
import {RenderPass} from "three/examples/jsm/postprocessing/RenderPass";
import {ThreeMobileHandler} from "./devices/device_mobile";
import {SceneHandler} from "./handlers/scene_handler";
import {ActionLogicHandler} from "./handlers/action_logic_handler";
import {ThreeRTCManager} from "./handlers/rtc_manager";

export class ThreeRenderer extends AlphaBatem {


	//Spawn position offsets
	initialOffsets = {
		0: {x: 0, y: 0, z: 0},
		1: {x: 0, y: 0, z: 0},
		2: {x: 0, y: 0, z: 0},
		3: {x: 0, y: 0, z: 0},
		4: {x: 0, y: 1, z: 1},
		5: {x: 0, y: 1, z: 1},
		6: {x: 0, y: 0, z: 0}, //3D3RD
		// 4: {x: 1.5, y: 2.5, z: 7.1}
	}

	//Holds requestFrameID for cancellation
	frame = null;

	isLoaded = false;

	gui = null
	dragControls = null;
	controls = null;
	mouse = {x: 0, y: 0};
	raycaster = new THREE.Raycaster();

	renderer = null
	composer = null;


	clock = null
	scene = null
	camera = null

	envMap = null;

	localEntity = null;

	controllers = []; //Custom logic controllers

	draggables = [];

	playerManager = null;
	animationManager = null;

	labelRenderer = null;

	//Movements to be applied
	movements = [];

	// physicsHandler = new PhysicsHandler();
	collisionHandler = null;
	rtcHandler = null;
	interactionHandler = null;
	actionLogicHandler = null;

	entityAddedEvent = (e) => {
		console.log("Entity added: ", e);
	}

	constructor() {
		super();

		this.load() //Load to init scene etc
		this.setupEventListeners();
		this.setupManagers()

		this.animate()
	}

	setupManagers() {
		this.addManager("rtc", new ThreeRTCManager())
		this.rtcHandler = this.getManager("rtc")

		//3JS Managers for item,players & adverts
		this.addManager("player", new PlayerHandler(this.getModelManager(), this.getPositionManager()))
		this.playerManager = this.getManager("player")

		this.addManager("item", new ItemHandler(this.getModelManager()))
		this.addManager("advert", new AdvertisingHandler(this.getModelManager(), this.getConnectionManager(), this.scene))
		// this.addManager("physics", new PhysicsHandler())

		this.addManager("collision", new CollisionHandler(this.scene, this.camera, this.controls))
		this.collisionHandler = this.getManager("collision")

		//Bind collision to dialogue
		this.getManager("dialogue").setHandler("dialogue_open", () => {
			this.collisionHandler.setInteracting(true)
		})
		this.getManager("dialogue").setHandler("dialogue_close", () => {
			this.collisionHandler.setInteracting(false)
		})


		this.addManager("animation", new AnimationHandler())
		this.animationManager = this.getManager("animation")

		this.addManager("interaction", new InteractionHandler(this.renderer.domElement, this.scene, this.camera, {
			"collision": this.getManager("collision"),
			"animation": this.getManager("animation"),
			"rtc": this.getManager("rtc")
		}))
		this.interactionHandler = this.getManager("interaction")

		this.addManager("logic", new ActionLogicHandler({}, this.interactionHandler, this.getDialogueManager()))
		this.addManager("advertising", new AdvertisingHandler(this.getModelManager(), this.getConnectionManager(), this.scene))

		// this.physicsHandler = this.getManager("physics")


		this.collisionHandler.setSpawnPositions(this.initialOffsets)

		//Create our local target marker for player
		this.getManager("player").createLocalTarget(this.scene)
	}


	init() {
		super.init();
		this.setupControls();
		this.interactionHandler.setPlaceholder("$wallet_addr", this.getConnectionManager().getWalletAddr())
	}


	setupControls() {
		//Setup controls
		this.deviceHandler.bindCollision(this.collisionHandler)
		this.controls = this.deviceHandler.bindControls(this.camera, this.renderer.domElement, this.scene)

		// if (this.deviceHandler.isFreeFly())
		//     this.controls.addEventListener('change', (e) => this.onCameraMove(e));
		this.collisionHandler.setControls(this.controls)
		this.setupTransformControls()
		// this.setupDragControls([])
	}

	setRoom(roomID) {
		super.setRoom(roomID);
		this.getRoomManager().configureRoom(this) //Bind room listeners for loading room
	}

	/**
	 * Returns the html element that should be used as the container for all views
	 * @returns {HTMLElement}
	 */
	getContainer() {
		return document.getElementById("canvas-container") === null ? document.body : document.getElementById("canvas-container")
	}

	reset() {
		super.reset();

		for (let i = 0; i < this.controllers.length; i++) {
			this.controllers[i].reset()
		}
		this.clear();

		if (this.rtcHandler && this.rtcHandler.isConnected())
			this.rtcHandler.leaveMeeting()

		this.load() //Load to init scene etc
		this.setupEventListeners();
		this.setupManagers()
		this.setupControls()

		this.animate()
	}

	_resetLabelRenderer() {
		this.labelRenderer.domElement.remove()
		this.labelRenderer = null
	}


	load() {
		if (this.isLoaded)
			return
		this.isLoaded = true

		this.gui = new GUI()

		const container = this.getContainer()


		this.clock = new THREE.Clock()
		this.scene = new SceneHandler();

		this.labelRenderer = new CSS2DRenderer();
		this.labelRenderer.setSize(window.innerWidth, window.innerHeight);
		this.labelRenderer.domElement.classList.add("label-renderer");
		this.labelRenderer.domElement.style.position = 'absolute';
		this.labelRenderer.domElement.style.top = '0px';
		this.labelRenderer.domElement.style.pointerEvents = 'none';

		//Setup camera
		this.camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 600);
		this.camera.position.set(0, 2, 0)

		//Renderer
		this.renderer = new THREE.WebGLRenderer({antialias: false});
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.powerPreference = "high-performance";
		this.renderer.physicallyCorrectLights = true
		this.renderer.info.physicallyCorrectLights = true
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.outputEncoding = THREE.sRGBEncoding;

		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = THREE.BasicShadowMap; // default THREE.PCFShadowMap


		//Add to DOM
		container.appendChild(this.renderer.domElement);
		container.appendChild(this.labelRenderer.domElement);

		//Postprocessing
		this.composer = new EffectComposer(this.renderer);
		this.composer.setSize(window.innerWidth, window.innerHeight);

		//Base Renderer
		const renderScene = new RenderPass(this.scene.raw(), this.camera);


		//Add Base pass
		this.composer.addPass(renderScene);
	}

	getScene() {
		return this.scene;
	}

	addController(controller) {
		this.controllers.push(controller)
	}


	animate() {
		const dt = this.clock.getDelta();
		this._loopUpdate(dt);
		this._loopRender(dt)
	}


	setupEventListeners() {
		console.log("ThreeRender::setupEventListeners")

		window.addEventListener('resize', (e) => this.onWindowResize(e));
		this.renderer.domElement.addEventListener('dblclick', (e) => this.raycast(e), false);

		// this.renderer.domElement.addEventListener('contextmenu', (e) => {
		//     e.preventDefault();
		//     this.raycastMovement(e)
		// }, false);
	}

	lastUpdate = 0;
	lastPos = null
	updateTimeout = 200;
	sendNext = false

	_loopUpdate(delta) {
		//Local device handler - TODO Move to entity?
		if (this.deviceHandler !== null && this.deviceHandler !== undefined) {

			//Update collisions
			this.collisionHandler.update(delta, this.deviceHandler.getAngle())
			const newPos = this.deviceHandler.getPosition()

			if (this.lastUpdate + this.updateTimeout < performance.now() && newPos !== null) {

				const isEqual = this.lastPos === null || (newPos.x.toFixed(1) === this.lastPos.x.toFixed(1) && newPos.y.toFixed(1) === this.lastPos.y.toFixed(1) && newPos.z.toFixed(1) === this.lastPos.z.toFixed(1))

				if (this.lastPos === null || !isEqual || this.sendNext) {
					this.getConnectionManager().wsLocalMove(newPos, this.deviceHandler.getRotation())
					this.lastUpdate = performance.now()
					this.lastPos = newPos

					this.sendNext = !this.sendNext; //send the next pos change too if we have just changed (fixes weird 1 step lag)

				}
			}

			this.deviceHandler.animate(delta, (pos, rotation) => {
				this.onCameraMove(pos, rotation) //TODO move to localCameraMove and add timer here instead (this isnt used i dont think now)
			});
		}

		if (this.animationManager !== null)
			this.animationManager.update(delta)

		if (this.playerManager !== null)
			this.playerManager.update(delta)

		if (this.interactionHandler !== null)
			this.interactionHandler.update(delta, this.playerManager.localPlayer)

		if (this.getManager('npc'))
			this.getManager('npc').update(this.playerManager.localPlayer)


		for (let i = 0; i < this.controllers.length; i++) {
			this.controllers[i].update(delta)
		}

		//TODO for some reason update is undefined
		//Loop through entities and update movements
		// for (let ent of Object.keys(this.getEntityManager().all())) {
		//         this.getEntityManager().get(ent).update()
		// }

		if (this.labelRenderer !== null)
			this.labelRenderer.render(this.scene.raw(), this.camera);

		//Animations
		this.getEntityManager().animate(delta)
	}

	_loopRender(delta) {
		this.frame = requestAnimationFrame(() => this.animate());


		if (this.composer === null) {
			return
		}

		this.composer.render(delta);
		// this.renderer.render(this.scene, this.camera);
	}


	raycast(e) {
		const is = this._raycast(e)

		if (is.length > 0) {
			console.log(is[is.length - 1]);
			const pt = is[is.length - 1].point;
			pt.h_type = "ptr" //Hacky identifier
			// this.getConnectionManager().wsLocalItemPlace(pt)
		}

		return is
	}

	/**
	 * TODO Raycast from position?
	 * @param e
	 */
	raycastMovement(e) {
		const is = this._raycast(e)
		console.log("Move Raycast", is)
		if (is.length > 0) {
			const lastIntersect = is[0]
			if (lastIntersect.distance > 1 && lastIntersect.object.userData.isTerrain)
				this.deviceHandler.setTarget(lastIntersect.point, (pos, rotation) => {
					this.onCameraMove(pos, rotation)
				})
		}

	}

	_raycast(e) {
		//1. sets the mouse position with a coordinate system where the center
		//   of the screen is the origin
		this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;

		//2. set the picking ray from the camera position and mouse coordinates
		this.raycaster.setFromCamera(this.mouse, this.camera);

		//3. compute intersections
		let intersects = this.raycaster.intersectObjects(this.scene.getChildren());

		let is = [];

		console.debug("Raycast intersections", intersects)
		for (let i = 0; i < intersects.length; i++) {
			if (intersects[i].object.type === 'Mesh' && intersects[i].distance > 0.1 && intersects[i].object.userData.isTerrain) {
				is.push(intersects[i]);
				break
			}
		}

		return is;
	}

	localMove(tform) {
		const obj = tform.object

		console.log("localMove", {
			tform: tform,
			obj: obj
		})

		const pt = {
			uid: obj.name,
			position: obj.position,
			rotation: obj.rotation,
			scale: obj.scale,
		};

		if (obj.name === "sprite")
			pt.uid = obj.parent.name;

		pt.h_type = "item_move" //Hacky identifier
		console.log("ThreeRender::localMove", pt)
		// this.getConnectionManager().wsLocalItemMove(pt.uid, pt)
	}

	onWindowResize() {
		console.log(`OnWindowResize ${window.outerWidth}x${window.outerHeight}`)
		if (this.camera != null) {
			this.camera.aspect = window.outerWidth / window.outerHeight;
			this.camera.updateProjectionMatrix();
		}

		if (this.renderer != null) {
			this.renderer.setSize(window.outerWidth, window.outerHeight);
		}

		if (this.composer != null) {
			this.composer.setSize(window.outerWidth, window.outerHeight);
		}

		if (this.labelRenderer != null) {
			this.labelRenderer.setSize(window.outerWidth, window.outerHeight);
		}

	}

	onCameraMove(position = null, rotation = null) {
		console.log("onCameraMove", {
			position: position,
			rotation: rotation
		})
		if (position === null) {
			console.log(`onCameraMove::position*override`, this.camera.position)
			position = this.camera.position
		}

		if (rotation === null) {
			console.log(`onCameraMove::rotation*override`, this.camera.rotation)
			rotation = this.camera.rotation
		}
		console.log("onCameraMove", position, rotation)
		this.getConnectionManager().wsLocalMove(position, rotation)
	}

	onModelsLoaded() {
		super.onModelsLoaded();
	}

	onPlayerAdd(data) {
		const np = this.initialOffsets[data.device_type]

		console.log("Adding new player at position: ", {
			data: data,
			position: np,
		})

		//Pointless
		const plr = this.getPositionManager().addPlayer(data.token_addr, np, data.device_type)

		const localUID = this.getConnectionManager().getConnectionUUID()
		const isLocal = localUID === data.user_id;

		if (!this.managers["player"].deviceModelMap[data.device_type]) {
			console.warn("No character set for device: ", data.device_type)
			return
		}

		//Entity
		let model = this.managers["player"].deviceModelMap[data.device_type]


		console.log("onPlayerAdd::model - local:" + isLocal, data.token_addr, model, data.device_type)
		const ent = new ThreePlayer(data.user_id, model, isLocal, data.device_type) //TODO Check
		ent.setTokenAddress(data.token_addr)
		ent.setUsername(data.username)


		let ps = this.managers["player"].modelScaleMap["default_sprite"]

		if (data.custom_sprite !== undefined && data.custom_sprite !== '') {
			console.log("onPlayerAdd::setCustomSprite", data.custom_sprite, data)
			model = `player_custom_${data.token_addr}`
			ent.setModelKey(model)
			ent.setCustomSprite(data.custom_sprite)
			// ps = this.managers["player"].modelScaleMap["custom_sprite"]
		}

		//Add as Player
		this.managers["player"].handlePlayerEntityAdd(ent, isLocal)

		//Add Player to entity list
		this.getEntityManager().addEntity(ent)

		console.log("onPlayerAdd::scale", ps)
		//Add entity to scene
		ent.setScale(ps)
		ent.setPosition(np)
		ent.add(this.getModelManager(), this.scene, () => {
			ent.setScale(ps)
			console.log("Entity added:callback", ent, ps)
			ent.obj.rotateY(Math.PI)
			ent.obj.userData.alphaID = ent.uid
			ent.obj.userData.token_addr = ent.getTokenAddress()

			// ent.obj.updateMatrixWorld()
			// this.setShadows(ent.obj)
			ent.obj.position.set(np.x, np.y, np.z)
			if (data.last_action_at) {
				console.log("Existing entity, updating true position")
				ent.setPosition(data.position)
				ent.obj.position.set(data.position.x, data.position.y, data.position.z)

				if (data.rotation)
					ent.obj.rotation.set(data.rotation.x, data.rotation.y, data.rotation.z)
			}

			// const box = new THREE.BoxHelper( ent.obj, 0xffff00 );
			// ent.obj.attach( box );

			if (isLocal) {
				this.localEntity = ent
				this.deviceHandler.setPlayer(ent)
				this.collisionHandler.addLocalPlayerCollider(ent)
			} else {
				this.getInteractionHandler().addInteractable(ent.obj, {
					click: {
						action_type: 'show_profile',
						data: {
							person: ent.uid,
						},
						on_error: null,
						on_success: null,
					},
				})
			}

			this.entityAddedEvent(ent)
		})
	}

	onPlayerRemove(data) {
		this.managers["player"].handlePlayerRemove(data.user_id, this.scene)

		this.getEntityManager().removeEntity(data.user_id)
	}

	onPlayerMove(data) {
		// if (this.getEntityManager().get(data.user_id))
		//     this.getEntityManager().get(data.user_id).move(data)
		this.managers["player"].handlePlayerMove(data)

		this.getEntityManager().updateEntity(data.player_uid, {
			position: data.position,
			rotation: data.rotation
		}, "walk")

	}

	onPlayerChat(data) {
		super.onPlayerChat(data);

		const ent = this.getEntityManager().get(data.sent_by)
		if (ent == null) {
			console.error("No entity found for player chat")
			return
		}

		ent.onChat(data.message)
	}

	onItemMove(data, movedBy) {
		console.log("ThreeRender::onItemMove", data, movedBy)


		//Detatch any items currently being controlled
		//TODO Do check for active UID instead of always detaching
		this.dragControls.detach()

		//Update entity position
		this.getEntityManager().updateEntity(data.uid, {
			position: data.position,
			rotation: data.rotation,
			scale: data.scale
		})

		const ent = this.getEntityManager().get(data.uid)
		if (ent == null) {
			console.error("No entity found for item move")
			return
		}
		ent.update(data)
	}

	onItemRemove(uid) {
		super.onItemRemove(uid);
		this.getEntityManager().removeEntity(uid);

		const obj = this.scene.getObjectByName(uid);
		this.scene.remove(obj)
	}

	getDeviceHandler(deviceType, demo) {
		console.log("getDeviceHandler", deviceType)
		switch (deviceType.toUpperCase()) {
			case "3D":
				return new Three3DHandler()
			case "3DMOBILE":
				return new ThreeMobileHandler(this.scene)
			case "3D3RD":
				return new ThreeThirdPersonHandler(this.scene)
			default:
			case "OBSERVER":
				return new ThreeObserverHandler()
			case "GENERAL_BUILDER":
				return new ThreeGeneralBuilderHandler()
			case "MINECRAFT":
				return new ThreeMinecraftHandler(demo)
			// case "VR":
			//     return new DeviceVRHandler()
			// case "AR":
			//     return new DeviceARHandler()
		}
	}

	setupTransformControls() {
		this.dragControls = new TransformControls(this.camera, this.renderer.domElement);

		this.dragControls.addEventListener('dragging-changed', (event) => {
			this.controls.enabled = !event.value;
		});

		this.dragControls.addEventListener('mouseUp', (event) => {
			if (event.target === null)
				return

			console.log("TransformControls::mouseUp", event.target)
			this.localMove(event.target)
		});

		this.scene.add(this.dragControls)
	}

	setupDragControls(obj) {
		if (this.dragControls != null) {
			this.dragControls.dispose()
		}

		console.log("Setting up drag controls: ", obj)
		this.dragControls = new DragControls(obj, this.camera, this.renderer.domElement);
		this.dragControls.transformGroup = true

		this.dragControls.addEventListener('dragstart', (event) => {
			console.log("ds", event)
			this.controls.enabled = false
			// event.object.material.emissive.set(0xaaaaaa);
		});

		this.dragControls.addEventListener('dragend', (event) => {
			console.log("de", event)
			this.controls.enabled = true
			//TODO Emit item move
			// this.getConnectionManager().wsLocalItemMove()

			// event.object.material.emissive.set(0x000000);
		});
	}

	onClick(event) {
		if (this.draggables.length === 0 || this.camera === null) {
			return
		}

		this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

		this.raycaster.setFromCamera(this.mouse, this.camera);


		// console.log("Checking click on draggables", this.draggables)
		const intersections = this.raycaster.intersectObjects(this.draggables, true);
		if (intersections.length === 0) {
			this.dragControls.detach()
			return;
		}

		console.log("intersections", intersections)
		for (let i = 0; i < intersections.length; i++) {
			intersections[i].object.traverseAncestors((obj) => {
				if (obj.name === "sprite") {
					console.log("Clicked", obj)
					this.dragControls.detach()
					this.dragControls.attach(obj)
					return false;
				}
			})
		}
	}

	toggleClickMode(mode = null) {
		if (mode === null)
			mode = this.dragControls.mode === 'translate' ? 'rotate' : 'translate'

		if (this.dragControls === null)
			return

		this.dragControls.setMode(mode)
		// switch(mode) {
		//     case "scale":
		//         this.dragControls.showX = false
		//         this.dragControls.showY = false
		//         this.dragControls.showZ = false
		//         break
		//     case "translate":
		//     case "rotate":
		//     default:
		//         this.dragControls.showX = true
		//         this.dragControls.showY = true
		//         this.dragControls.showZ = true
		//         break
		// }
	}

	removeActiveObject() {
		if (this.dragControls === null || this.dragControls.object === null || this.dragControls.object === undefined) {
			console.warn("No selected object to remove")
			return
		}

		console.log("removing active object", this.dragControls.object)

		//TODO Check FE perms
		let uid = this.dragControls.object.name
		if (uid === "sprite") {
			uid = this.dragControls.object.parent.name
		}

		const dat = {
			h_type: "item_remove",
			uid: uid,
		}

		this.dragControls.detach()
		this.getConnectionManager().wsLocalItemRemove(dat)
	}

	onDraggableClick(event) {
		if (this.dragControls === null)
			return

		if (this.draggables.length === 0) {
			return
		}

		const draggableObjects = this.dragControls.getObjects();
		draggableObjects.length = 0;

		this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

		this.raycaster.setFromCamera(this.mouse, this.camera);

		// console.log("Checking click on draggables", this.draggables)
		const intersections = this.raycaster.intersectObjects(this.draggables, true);

		// console.log("intersections",intersections)
		for (let i = 0; i < intersections.length; i++) {

			intersections[i].object.traverseAncestors((obj) => {
				if (obj.name === "sprite") {
					console.log("Clicked", obj)
					draggableObjects.push(obj)

					this.dragControls.dispatchEvent("dragstart", {
						object: obj,
						target: null,
					})
					return false;
				}
			})
		}
	}

	destroy() {
		super.destroy()
		this.clear()
	}

	clear() {
		if (this.frame !== null) {
			cancelAnimationFrame(this.frame)
			this.frame = null
		}
		this._resetLabelRenderer()

		this.renderer.dispose()
		this.renderer = null;
		this.camera = null;
		this.dragControls = null;
		this.controls = null;

		if (this.composer)
			this.composer.reset()

		if (this.rtcHandler && this.rtcHandler.isConnected())
			this.rtcHandler.leaveMeeting()

		this.collisionHandler = null;
		this.interactionHandler = null;
		this.rtcHandler = null;

		if (this.gui !== null) {
			this.gui.destroy()
		}

		this.gui = null
		this.clock = null
		this.scene = null
		this.envMap = null;
		this.localEntity = null;
		this.controllers = []; //Custom logic controllers
		this.draggables = [];
		this.playerManager = null;
		this.animationManager = null;
		this.movements = [];
	}

	/**
	 * Utils
	 */

	getCollisionHandler() {
		return this.collisionHandler
	}

	getInteractionHandler() {
		return this.interactionHandler
	}

	getActionLogicHandler() {
		return this.getManager("logic")
	}

	setShadows(obj) {
		if (obj.isMesh) {
			if (this.envMap !== null)
				obj.envMap = this.envMap;

			obj.castShadow = true
			obj.receiveShadow = true
		}

		obj.traverse((child) => {
			if (child.isMesh) {
				if (this.envMap !== null)
					child.envMap = this.envMap;

				child.castShadow = true
				child.receiveShadow = true
			}
		})
	}
}
